import { apiClient, API_ENDPOINTS } from '../../../config/api';
import type {
  NotificationItem,
  NotificationCategory,
  NotificationsResponse,
  UnreadCountResponse,
} from '../types/notifications';

// ---------------------------------------------------------------------------
// 서버 응답 타입
// ---------------------------------------------------------------------------
interface ServerNotificationItem {
  id: number;
  type: string;
  title: string;
  content: string;
  isRead: boolean;
  referenceType: string | null;
  referenceId: number | null;
  createdAt: string; // ISO 8601 (LocalDateTime serialized)
}

interface ServerNotificationsResponse {
  notifications: ServerNotificationItem[];
  totalCount: number;
  hasNext: boolean;
}

// ---------------------------------------------------------------------------
// 매핑 테이블
// ---------------------------------------------------------------------------
const MARKETING_TYPES = new Set([
  'RETENTION_14DAY',
  'RETENTION_21DAY',
  'CARD_NOT_OPENED',
]);

const TYPE_TO_CODE: Record<string, string> = {
  CARD_ISSUED: 'NTF-CARD-001',
  CARD_EXPIRING: 'NTF-CARD-002',
  CARD_EXPIRED: 'NTF-CARD-003',
  CARD_FINAL_WARNING: 'NTF-CARD-004',
  CARD_EXTENDED: 'NTF-CARD-005',
  CARD_OPENED_RECEIVED: 'NTF-MATCH-001',
  NEW_MESSAGE: 'NTF-CHAT-001',
  CHAT_EXPIRING: 'NTF-CHAT-002',
  HEART_CHARGED: 'NTF-PAY-001',
  HEART_INSUFFICIENT: 'NTF-PAY-002',
  HEART_DEPLETED: 'NTF-PAY-003',
  SIGNUP_WELCOME: 'NTF-ACC-001',
  PROFILE_REVIEW_APPROVED: 'NTF-ACC-002',
  PROFILE_REVIEW_REJECTED: 'NTF-ACC-003',
  PROFILE_UPDATE_APPROVED: 'NTF-ACC-004',
  PROFILE_UPDATE_REJECTED: 'NTF-ACC-005',
  SIGNUP_HEART_REWARD: 'NTF-ACC-006',
  PROFILE_COMPLETE_HEART_REWARD: 'NTF-ACC-007',
  IDENTITY_VERIFIED: 'NTF-ACC-008',
  RETENTION_14DAY: 'NTF-MKT-001',
  RETENTION_21DAY: 'NTF-MKT-002',
  CARD_NOT_OPENED: 'NTF-MKT-003',
};

// ---------------------------------------------------------------------------
// 딥링크 파생 (referenceType + referenceId + type)
// ---------------------------------------------------------------------------
function deriveDeeplink(
  type: string,
  referenceType: string | null,
  referenceId: number | null,
): string | undefined {
  if (referenceType === 'CONVERSATION' && referenceId != null) {
    return `/messages/${referenceId}`;
  }
  if (referenceType === 'HEART_WALLET') {
    return type === 'HEART_INSUFFICIENT' || type === 'HEART_DEPLETED'
      ? '/heart/purchase'
      : '/wallet';
  }
  if (referenceType === 'CUPID_CARD') return '/dashboard';
  if (referenceType === 'INQUIRY' && referenceId != null) {
    return `/support/inquiry/${referenceId}`;
  }

  // referenceType 없는 경우 type 기반 폴백
  switch (type) {
    case 'SIGNUP_WELCOME':
    case 'PROFILE_REVIEW_APPROVED':
    case 'CARD_ISSUED':
    case 'CARD_EXPIRING':
    case 'CARD_EXPIRED':
    case 'CARD_FINAL_WARNING':
    case 'CARD_EXTENDED':
    case 'CARD_OPENED_RECEIVED':
    case 'RETENTION_14DAY':
    case 'RETENTION_21DAY':
    case 'CARD_NOT_OPENED':
      return '/dashboard';
    case 'PROFILE_REVIEW_REJECTED':
      return '/register/profile';
    case 'PROFILE_UPDATE_APPROVED':
    case 'PROFILE_UPDATE_REJECTED':
    case 'IDENTITY_VERIFIED':
      return '/settings';
    case 'HEART_CHARGED':
    case 'SIGNUP_HEART_REWARD':
    case 'PROFILE_COMPLETE_HEART_REWARD':
      return '/wallet';
    case 'HEART_INSUFFICIENT':
    case 'HEART_DEPLETED':
      return '/heart/purchase';
    default:
      return undefined;
  }
}

// ---------------------------------------------------------------------------
// 서버 응답 → 클라이언트 타입 변환
// ---------------------------------------------------------------------------
function toClientItem(s: ServerNotificationItem): NotificationItem {
  const notificationId = (TYPE_TO_CODE[s.type] ?? s.type) as NotificationItem['notificationId'];
  const category: NotificationCategory = MARKETING_TYPES.has(s.type) ? 'MARKETING' : 'SERVICE';
  return {
    id: String(s.id),
    notificationId,
    title: s.title,
    body: s.content,
    isRead: s.isRead,
    createdAt: s.createdAt,
    deeplink: deriveDeeplink(s.type, s.referenceType, s.referenceId),
    category,
  };
}

// ---------------------------------------------------------------------------
// API 함수
// cursor = 페이지 번호 문자열 (0-based)
// ---------------------------------------------------------------------------
const PAGE_SIZE = 20;

export async function getNotifications(
  cursor?: string,
  size: number = PAGE_SIZE,
): Promise<NotificationsResponse> {
  const page = cursor != null ? Number(cursor) : 0;
  const qs = new URLSearchParams({
    page: String(page),
    size: String(size),
  });
  const res = await apiClient.get<{ result: ServerNotificationsResponse }>(
    `${API_ENDPOINTS.NOTIFICATION.LIST}?${qs}`,
  );
  const data = res.result;
  const items = data.notifications.map(toClientItem);
  const nextCursor = data.hasNext ? String(page + 1) : null;
  return { items, nextCursor, hasMore: data.hasNext };
}

export async function getUnreadCount(): Promise<UnreadCountResponse> {
  const res = await apiClient.get<{ result: { unreadCount: number } }>(
    API_ENDPOINTS.NOTIFICATION.UNREAD_COUNT,
  );
  return { unreadCount: res.result.unreadCount };
}

export async function markRead(id: string): Promise<void> {
  await apiClient.patch(API_ENDPOINTS.NOTIFICATION.READ(id));
}

export async function markAllRead(): Promise<void> {
  // 서버는 카테고리 구분 없이 전체 읽음 처리
  await apiClient.patch(API_ENDPOINTS.NOTIFICATION.READ_ALL);
}

export async function markAllSeen(): Promise<void> {
  // 서버에 /seen 엔드포인트 없음 — no-op
}
